import { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { ArrowLeft, Check, AlertTriangle, CheckCircle, Clock, FileText, ChevronDown, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/cn';
import { useFullFinal, useUpdateFullFinal, useCloseFullFinal } from '@/api/queries/document.queries';
import { useEmployees } from '@/api/queries/employee.queries';
import { selectUser } from '@/store/slices/authSlice';
import { Avatar } from '@/components/shared/AvatarWithInitials';
import { format, differenceInDays, differenceInYears, differenceInMonths } from 'date-fns';
import { toast } from 'sonner';

const STEPS = [
  { id: 1, key: 'INITIATION',   label: 'Initiation'     },
  { id: 2, key: 'CLEARANCE',    label: 'Clearance'      },
  { id: 3, key: 'COMPUTATION',  label: 'Computation'    },
  { id: 4, key: 'DOCUMENTS',    label: 'Documents'      },
  { id: 5, key: 'PF_STATUTORY', label: 'PF & Statutory' },
  { id: 6, key: 'CLOSED',       label: 'Confirm & Close'},
];

const DEFAULT_CHECKLIST = [
  { id: 'hod-1',   dept: 'HOD',     item: 'Handover of responsibilities completed',     status: 'PENDING' },
  { id: 'it-1',    dept: 'IT',      item: 'Laptop returned',                            status: 'PENDING' },
  { id: 'it-2',    dept: 'IT',      item: 'Phone/accessories returned',                 status: 'PENDING' },
  { id: 'it-3',    dept: 'IT',      item: 'Data backup & account deactivation',         status: 'PENDING' },
  { id: 'admin-1', dept: 'Admin',   item: 'ID card returned',                           status: 'PENDING' },
  { id: 'admin-2', dept: 'Admin',   item: 'Access card / keys returned',                status: 'PENDING' },
  { id: 'fin-1',   dept: 'Finance', item: 'Salary advance recovered',                   status: 'PENDING' },
  { id: 'fin-2',   dept: 'Finance', item: 'Outstanding loans cleared',                  status: 'PENDING' },
  { id: 'can-1',   dept: 'Canteen', item: 'Monthly canteen balance cleared',             status: 'PENDING' },
  { id: 'sec-1',   dept: 'Security','item': 'Gate pass returned',                       status: 'PENDING' },
];

function Step1Initiation({ ff }) {
  const lwd = ff.last_working_day ? new Date(ff.last_working_day) : null;
  const sep = ff.separation_date  ? new Date(ff.separation_date)  : null;
  const served   = lwd && sep ? differenceInDays(lwd, sep) : 0;
  const required = 30;
  const shortfall = Math.max(0, required - served);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        {[
          { label: 'Employee',         value: ff.employee_name },
          { label: 'Employee Code',    value: ff.employee_code },
          { label: 'Designation',      value: ff.designation },
          { label: 'Department',       value: ff.department },
          { label: 'Separation Type',  value: ff.separation_type },
          { label: 'Date of Joining',  value: ff.joining_date ? format(new Date(ff.joining_date), 'dd MMM yyyy') : '—' },
          { label: 'Separation Date',  value: sep ? format(sep, 'dd MMM yyyy') : '—' },
          { label: 'Last Working Day', value: lwd ? format(lwd, 'dd MMM yyyy') : '—' },
        ].map(({ label, value }) => (
          <div key={label} className="p-3 rounded-[10px] bg-[var(--bg-app)] border border-[var(--border)]">
            <p className="text-[10px] font-semibold text-[var(--text-tertiary)] uppercase tracking-wide">{label}</p>
            <p className="text-[13px] font-medium text-[var(--text-primary)] mt-0.5">{value || '—'}</p>
          </div>
        ))}
      </div>

      {/* Notice period summary */}
      <div className={cn('p-4 rounded-[12px] border', shortfall > 0 ? 'bg-amber-50 border-amber-200' : 'bg-green-50 border-green-200')}>
        <div className="flex items-center gap-2 mb-2">
          {shortfall > 0
            ? <AlertTriangle size={15} className="text-amber-600 shrink-0" />
            : <CheckCircle size={15} className="text-green-600 shrink-0" />}
          <p className={cn('text-[13px] font-semibold', shortfall > 0 ? 'text-amber-700' : 'text-green-700')}>
            Notice Period: {shortfall > 0 ? `${shortfall} days shortfall` : 'Fully served'}
          </p>
        </div>
        <div className="grid grid-cols-3 gap-3 text-[12px]">
          <div><span className="text-[var(--text-tertiary)]">Required:</span> <strong>{required} days</strong></div>
          <div><span className="text-[var(--text-tertiary)]">Served:</span> <strong>{served} days</strong></div>
          <div><span className="text-[var(--text-tertiary)]">Shortfall:</span> <strong className={shortfall > 0 ? 'text-amber-600' : 'text-green-600'}>{shortfall} days</strong></div>
        </div>
      </div>
    </div>
  );
}

function Step2Clearance({ ff, onUpdate }) {
  const checklist  = ff.checklist || DEFAULT_CHECKLIST;
  const user       = useSelector(selectUser);

  const toggle = (itemId, newStatus) => {
    const updated = checklist.map(c =>
      c.id === itemId ? { ...c, status: newStatus, cleared_by: user?.name || 'HR', cleared_at: new Date().toISOString() } : c
    );
    onUpdate({ checklist: updated });
  };

  const waive = (itemId) => {
    const updated = checklist.map(c => c.id === itemId ? { ...c, status: 'WAIVED' } : c);
    onUpdate({ checklist: updated });
  };

  const byDept = checklist.reduce((acc, item) => {
    if (!acc[item.dept]) acc[item.dept] = [];
    acc[item.dept].push(item);
    return acc;
  }, {});

  const cleared = checklist.filter(c => c.status === 'CLEARED' || c.status === 'WAIVED').length;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 p-3 rounded-[10px] bg-[var(--bg-app)] border border-[var(--border)]">
        <div className="flex-1">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[12px] font-medium text-[var(--text-primary)]">Clearance Progress</span>
            <span className="text-[12px] font-semibold text-[var(--accent)]">{cleared}/{checklist.length}</span>
          </div>
          <div className="h-2 bg-[var(--border)] rounded-full overflow-hidden">
            <div className="h-full bg-[var(--accent)] rounded-full transition-all"
              style={{ width: `${Math.round((cleared / checklist.length) * 100)}%` }} />
          </div>
        </div>
      </div>

      {Object.entries(byDept).map(([dept, items]) => (
        <div key={dept}>
          <p className="text-[11px] font-semibold text-[var(--text-tertiary)] uppercase tracking-wide mb-2">{dept}</p>
          <div className="space-y-2">
            {items.map(item => (
              <div key={item.id}
                className={cn('flex items-center gap-3 p-3 rounded-[10px] border transition-colors',
                  item.status === 'CLEARED' ? 'bg-green-50 border-green-200' :
                  item.status === 'WAIVED'  ? 'bg-gray-50 border-gray-200' : 'bg-[var(--bg-card)] border-[var(--border)]')}>
                <div className={cn('w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0',
                  item.status === 'CLEARED' ? 'bg-green-500 border-green-500' :
                  item.status === 'WAIVED'  ? 'bg-gray-400 border-gray-400' : 'border-[var(--border)]')}>
                  {(item.status === 'CLEARED' || item.status === 'WAIVED') && <Check size={11} className="text-white" />}
                </div>
                <div className="flex-1">
                  <p className={cn('text-[12px] font-medium', item.status === 'CLEARED' ? 'text-green-700' : 'text-[var(--text-primary)]')}>{item.item}</p>
                  {item.cleared_by && <p className="text-[10px] text-[var(--text-tertiary)]">Cleared by {item.cleared_by}</p>}
                </div>
                {item.status === 'PENDING' && (
                  <div className="flex items-center gap-1.5">
                    <button onClick={() => toggle(item.id, 'CLEARED')}
                      className="h-7 px-2.5 text-[11px] font-medium bg-green-500 text-white rounded-[6px] hover:bg-green-600 transition-colors">
                      Clear
                    </button>
                    <button onClick={() => waive(item.id)}
                      className="h-7 px-2.5 text-[11px] font-medium border border-[var(--border)] text-[var(--text-tertiary)] rounded-[6px] hover:bg-[var(--bg-hover)] transition-colors">
                      Waive
                    </button>
                  </div>
                )}
                {item.status !== 'PENDING' && (
                  <button onClick={() => toggle(item.id, 'PENDING')}
                    className="h-7 px-2.5 text-[11px] font-medium border border-[var(--border)] text-[var(--text-tertiary)] rounded-[6px] hover:bg-[var(--bg-hover)] transition-colors">
                    Undo
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function Step3Computation({ ff, onUpdate }) {
  const gross = ff.gross_monthly || 0;
  const basic = ff.basic_monthly || 0;
  const comp = ff.computation || {};

  const joinDate = ff.joining_date ? new Date(ff.joining_date) : null;
  const lwdDate  = ff.last_working_day ? new Date(ff.last_working_day) : new Date();
  const yearsOfService = joinDate ? differenceInYears(lwdDate, joinDate) : 0;
  const monthsOfService = joinDate ? differenceInMonths(lwdDate, joinDate) : 0;

  // Auto calculations
  const lwd       = ff.last_working_day ? new Date(ff.last_working_day) : new Date();
  const workDays  = lwd ? lwd.getDate() : 0;
  const lastMonthSalary = gross > 0 ? Math.round((gross / 30) * workDays) : 0;
  const pendingEL = comp.pending_el || 0;
  const maxEncash = 30;
  const elEncash  = basic > 0 ? Math.round((basic / 26) * Math.min(pendingEL, maxEncash)) : 0;

  const isGratuityEligible = yearsOfService >= 5;
  const gratuity = isGratuityEligible ? Math.min(Math.round((15 * basic) / 26 * yearsOfService), 2000000) : 0;

  const sep    = ff.separation_date ? new Date(ff.separation_date) : null;
  const served = sep && lwd ? differenceInDays(lwd, sep) : 30;
  const shortfall = Math.max(0, 30 - served);
  const noticeDed = gross > 0 ? Math.round((gross / 30) * shortfall) : 0;

  const totalEarnings    = lastMonthSalary + elEncash + gratuity + (comp.bonus || 0) + (comp.other_earnings || 0);
  const totalDeductions  = noticeDed + (comp.outstanding_advances || 0) + (comp.asset_recovery || 0) + (comp.other_deductions || 0);
  const netFF            = totalEarnings - totalDeductions;

  const set = (k, v) => onUpdate({ computation: { ...comp, [k]: v } });

  const Row = ({ label, value, editable, editKey, highlight, deduction }) => (
    <tr className={cn('border-b border-[var(--border)]', highlight ? 'bg-[var(--accent-muted)]' : '')}>
      <td className="px-4 py-2.5 text-[12px] text-[var(--text-secondary)]">{label}</td>
      <td className="px-4 py-2.5 text-right text-[12px]">
        {editable ? (
          <input type="number" value={comp[editKey] || ''}
            onChange={e => set(editKey, Number(e.target.value))}
            className="w-32 h-7 px-2 text-right text-[12px] border border-[var(--border)] rounded-[6px] bg-[var(--bg-app)] focus:outline-none focus:border-[var(--accent)]" />
        ) : (
          <span className={cn('font-medium', deduction ? 'text-red-500' : 'text-[var(--text-primary)]', highlight ? 'text-[var(--accent)]' : '')}>
            {value < 0 ? '—' : `₹${value.toLocaleString('en-IN')}`}
          </span>
        )}
      </td>
    </tr>
  );

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-3">
        <div className="p-3 rounded-[10px] bg-[var(--bg-app)] border border-[var(--border)]">
          <p className="text-[10px] text-[var(--text-tertiary)]">Years of Service</p>
          <p className="text-[16px] font-bold text-[var(--text-primary)]">{yearsOfService}y {monthsOfService % 12}m</p>
        </div>
        <div className="p-3 rounded-[10px] bg-[var(--bg-app)] border border-[var(--border)]">
          <p className="text-[10px] text-[var(--text-tertiary)]">Gross Monthly</p>
          <p className="text-[16px] font-bold text-[var(--text-primary)]">₹{gross.toLocaleString('en-IN')}</p>
        </div>
        <div className="p-3 rounded-[10px] bg-[var(--bg-app)] border border-[var(--border)]">
          <p className="text-[10px] text-[var(--text-tertiary)]">Basic Monthly</p>
          <p className="text-[16px] font-bold text-[var(--text-primary)]">₹{basic.toLocaleString('en-IN')}</p>
        </div>
      </div>

      <div className="p-3 rounded-[10px] bg-[var(--bg-app)] border border-[var(--border)]">
        <label className="text-[11px] font-semibold text-[var(--text-tertiary)] mb-1 block">Pending Earned Leave (EL) days</label>
        <input type="number" value={comp.pending_el || ''} onChange={e => set('pending_el', Number(e.target.value))}
          placeholder="Enter pending EL days from leave ledger"
          className="h-8 px-3 text-[12px] w-48 border border-[var(--border)] rounded-[8px] bg-white focus:outline-none focus:border-[var(--accent)]" />
      </div>

      <div className="card p-0 overflow-hidden">
        <table className="w-full text-[12px]">
          <tbody>
            <tr className="bg-[var(--bg-app)]">
              <td colSpan={2} className="px-4 py-2 text-[11px] font-semibold text-[var(--text-tertiary)] uppercase tracking-wide">Earnings</td>
            </tr>
            <Row label={`Last Month Salary (${workDays} days × ₹${Math.round(gross/30).toLocaleString('en-IN')}/day)`} value={lastMonthSalary} />
            <Row label={`EL Encashment (${Math.min(pendingEL, maxEncash)} days × Basic/26)`} value={elEncash} />
            {isGratuityEligible
              ? <Row label={`Gratuity (15 × Basic/26 × ${yearsOfService} yrs — capped ₹20L)`} value={gratuity} />
              : <tr className="border-b border-[var(--border)]">
                  <td colSpan={2} className="px-4 py-2.5 text-[11px] text-amber-600">
                    ⚠ Gratuity not applicable — employee has less than 5 years of service ({yearsOfService}y {monthsOfService%12}m)
                  </td>
                </tr>
            }
            <Row label="Bonus (if applicable)" editable editKey="bonus" value={comp.bonus || 0} />
            <Row label="Other Earnings" editable editKey="other_earnings" value={comp.other_earnings || 0} />
            <tr className="bg-green-50 border-b border-green-200">
              <td className="px-4 py-2.5 text-[12px] font-semibold text-green-700">Total Earnings</td>
              <td className="px-4 py-2.5 text-right text-[14px] font-bold text-green-700">₹{totalEarnings.toLocaleString('en-IN')}</td>
            </tr>
            <tr className="bg-[var(--bg-app)]">
              <td colSpan={2} className="px-4 py-2 text-[11px] font-semibold text-[var(--text-tertiary)] uppercase tracking-wide">Deductions</td>
            </tr>
            <Row label={`Notice Period Shortfall (${shortfall} days × ₹${Math.round(gross/30).toLocaleString('en-IN')}/day)`} value={noticeDed} deduction />
            <Row label="Outstanding Advances" editable editKey="outstanding_advances" value={comp.outstanding_advances || 0} deduction />
            <Row label="Asset Recovery (Lost/Damaged)" editable editKey="asset_recovery" value={comp.asset_recovery || 0} deduction />
            <Row label="Other Deductions" editable editKey="other_deductions" value={comp.other_deductions || 0} deduction />
            <tr className="bg-red-50 border-b border-red-200">
              <td className="px-4 py-2.5 text-[12px] font-semibold text-red-600">Total Deductions</td>
              <td className="px-4 py-2.5 text-right text-[14px] font-bold text-red-600">₹{totalDeductions.toLocaleString('en-IN')}</td>
            </tr>
            <tr className="bg-[var(--accent-muted)]">
              <td className="px-4 py-3 text-[14px] font-bold text-[var(--accent)]">NET F&F AMOUNT</td>
              <td className="px-4 py-3 text-right text-[18px] font-bold text-[var(--accent)]">₹{netFF.toLocaleString('en-IN')}</td>
            </tr>
          </tbody>
        </table>
        <div className="px-4 py-2.5 text-[11px] text-amber-600 bg-amber-50 border-t border-amber-200">
          ⚠ TDS (Income Tax) will be applicable on F&F components as per applicable income tax slab. Consult your CA before final payment.
        </div>
      </div>
    </div>
  );
}

function Step4Documents({ ff }) {
  const navigate = useNavigate();
  const docs = [
    { key: 'experience_cert', name: 'Experience Certificate', tplId: 'tpl-experience-certificate', required: true },
    { key: 'relieving_letter', name: 'Relieving Letter', tplId: 'tpl-relieving-letter', required: true },
    { key: 'service_cert', name: 'Service Certificate', tplId: 'tpl-service-certificate', required: false },
    { key: 'noc_letter', name: 'NOC Letter', tplId: 'tpl-noc-letter', required: false },
  ];

  return (
    <div className="space-y-3">
      <p className="text-[13px] text-[var(--text-secondary)]">Generate and issue exit documents for <strong>{ff.employee_name}</strong></p>
      {docs.map(doc => (
        <div key={doc.key}
          className="flex items-center justify-between p-3 rounded-[10px] border border-[var(--border)] hover:border-[var(--accent)]/30 transition-colors">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-[var(--bg-app)] flex items-center justify-center">
              <FileText size={15} className="text-[var(--text-tertiary)]" />
            </div>
            <div>
              <p className="text-[13px] font-medium text-[var(--text-primary)]">{doc.name}</p>
              {doc.required && <p className="text-[10px] text-amber-600 font-medium">Required</p>}
            </div>
          </div>
          <button
            onClick={() => navigate(`/documents/issue?template=${doc.tplId}&employee=${ff.employee_id}`)}
            className="h-7 px-3 text-[11px] font-medium bg-[var(--accent)] text-white rounded-[7px] hover:bg-[var(--accent-hover)] transition-colors">
            Generate & Issue
          </button>
        </div>
      ))}
    </div>
  );
}

function Step5PF({ ff, onUpdate }) {
  const pf = ff.pf_statutory || {};
  const set = (k, v) => onUpdate({ pf_statutory: { ...pf, [k]: v } });

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="form-label">UAN Number</label>
          <input value={pf.uan || ''} onChange={e => set('uan', e.target.value)} placeholder="12-digit UAN" className="input-field" />
        </div>
        <div>
          <label className="form-label">PF Settlement Type</label>
          <select value={pf.settlement_type || 'WITHDRAWAL'} onChange={e => set('settlement_type', e.target.value)} className="input-field">
            <option value="WITHDRAWAL">Withdrawal (Form 19)</option>
            <option value="TRANSFER">Transfer (Form 13)</option>
          </select>
        </div>
        <div>
          <label className="form-label">PF Status</label>
          <select value={pf.pf_status || 'NOT_FILED'} onChange={e => set('pf_status', e.target.value)} className="input-field">
            <option value="NOT_FILED">Not Filed</option>
            <option value="FILED">Filed</option>
            <option value="RECEIVED">Received by Employee</option>
          </select>
        </div>
        <div>
          <label className="form-label">ESIC Dues</label>
          <select value={pf.esic_dues || 'PENDING'} onChange={e => set('esic_dues', e.target.value)} className="input-field">
            <option value="PENDING">Pending</option>
            <option value="CLEARED">Cleared</option>
            <option value="NOT_APPLICABLE">Not Applicable</option>
          </select>
        </div>
        <div className="col-span-2">
          <label className="form-label">Notes</label>
          <textarea value={pf.notes || ''} onChange={e => set('notes', e.target.value)} rows={3}
            placeholder="Any notes about PF/ESIC settlement…" className="input-field resize-none" />
        </div>
      </div>
      <div className="p-3 rounded-[10px] bg-blue-50 border border-blue-200 text-[12px] text-blue-700">
        <strong>Reminder:</strong> Employee must submit Form 19 (for withdrawal) or Form 13 (for transfer) to EPFO.
        UAN-based withdrawal can be done online at unifiedportal-mem.epfindia.gov.in.
      </div>
    </div>
  );
}

function Step6Confirm({ ff, onClose }) {
  const closeFF   = useCloseFullFinal();
  const [confirm, setConfirm] = useState(false);
  const checklist = ff.checklist || DEFAULT_CHECKLIST;
  const cleared   = checklist.filter(c => c.status === 'CLEARED' || c.status === 'WAIVED').length;
  const allCleared = cleared === checklist.length;

  const handleClose = () => {
    if (!allCleared) { toast.error('Complete all clearances before closing F&F'); return; }
    closeFF.mutate({ empId: ff.employee_id, final_status: ff.separation_type === 'Termination' ? 'TERMINATED' : 'RESIGNED' }, {
      onSuccess: () => {
        toast.success(`F&F closed. ${ff.employee_name} has been deactivated.`);
        onClose();
      },
    });
  };

  return (
    <div className="space-y-4">
      <div className="p-4 rounded-[12px] border border-[var(--border)] space-y-2">
        <h3 className="text-[13px] font-semibold text-[var(--text-primary)]">F&F Summary</h3>
        <div className="grid grid-cols-2 gap-2 text-[12px]">
          <div><span className="text-[var(--text-tertiary)]">Employee:</span> <strong>{ff.employee_name}</strong></div>
          <div><span className="text-[var(--text-tertiary)]">Last Working Day:</span> <strong>{ff.last_working_day ? format(new Date(ff.last_working_day), 'dd MMM yyyy') : '—'}</strong></div>
          <div><span className="text-[var(--text-tertiary)]">Clearances:</span> <strong className={allCleared ? 'text-green-600' : 'text-amber-600'}>{cleared}/{checklist.length}</strong></div>
          <div><span className="text-[var(--text-tertiary)]">Separation:</span> <strong>{ff.separation_type}</strong></div>
        </div>
      </div>

      {!allCleared && (
        <div className="p-3 rounded-[10px] bg-amber-50 border border-amber-200 flex items-center gap-2 text-[12px] text-amber-700">
          <AlertTriangle size={14} className="shrink-0" />
          <span>{checklist.length - cleared} clearances still pending. Please complete all clearances before closing F&F.</span>
        </div>
      )}

      <div className="flex items-start gap-2.5 p-3 rounded-[10px] border border-[var(--border)]">
        <input type="checkbox" id="confirm-ff" checked={confirm} onChange={e => setConfirm(e.target.checked)}
          className="mt-0.5 h-4 w-4 rounded" />
        <label htmlFor="confirm-ff" className="text-[12px] text-[var(--text-secondary)] cursor-pointer">
          I confirm that all clearances are complete, the final settlement amount has been approved, and I authorise the deactivation of <strong>{ff.employee_name}</strong>'s account.
        </label>
      </div>

      <div className="p-3 rounded-[10px] bg-red-50 border border-red-200 text-[12px] text-red-600">
        <strong>Warning:</strong> Closing F&F will change the employee's status to <strong>{ff.separation_type === 'Termination' ? 'TERMINATED' : 'RESIGNED'}</strong> and deactivate their access. This action cannot be undone.
      </div>

      <button onClick={handleClose} disabled={!confirm || !allCleared || closeFF.isPending}
        className="w-full h-10 bg-red-600 text-white text-[13px] font-semibold rounded-[10px] hover:bg-red-700 disabled:opacity-40 transition-colors">
        {closeFF.isPending ? 'Closing F&F…' : 'Confirm & Close F&F — Deactivate Employee'}
      </button>
    </div>
  );
}

export default function FullFinalWorkflowPage() {
  const { empId } = useParams();
  const navigate  = useNavigate();
  const { data, refetch } = useFullFinal(empId);
  const updateFF  = useUpdateFullFinal();
  const ff        = data?.data;

  const currentStepIdx = STEPS.findIndex(s => s.key === ff?.stage) ?? 0;
  const [activeStep, setActiveStep] = useState(null);
  const step = activeStep ?? Math.max(0, currentStepIdx);

  const handleUpdate = (updates) => {
    updateFF.mutate({ empId, ...updates }, {
      onSuccess: () => { refetch(); toast.success('Saved'); },
    });
  };

  const nextStep = () => {
    if (step < STEPS.length - 1) {
      const nextKey = STEPS[step + 1].key;
      handleUpdate({ stage: nextKey });
      setActiveStep(step + 1);
    }
  };

  if (!ff) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-[var(--accent)] border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-[13px] text-[var(--text-secondary)]">Loading F&F details…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="px-5 py-4 border-b border-[var(--border)] bg-[var(--bg-card)] shrink-0">
        <div className="flex items-center gap-3 mb-3">
          <button onClick={() => navigate('/documents/full-final')}
            className="h-8 w-8 flex items-center justify-center rounded-[8px] hover:bg-[var(--bg-hover)] text-[var(--text-secondary)]">
            <ArrowLeft size={16} />
          </button>
          <div className="flex items-center gap-2.5">
            <Avatar name={ff.employee_name} size="sm" />
            <div>
              <h1 className="text-[16px] font-bold text-[var(--text-primary)]">F&F — {ff.employee_name}</h1>
              <p className="text-[11px] text-[var(--text-tertiary)]">{ff.employee_code} · {ff.designation} · {ff.department}</p>
            </div>
          </div>
          {ff.last_working_day && (
            <div className="ml-auto text-right">
              <p className="text-[11px] text-[var(--text-tertiary)]">Last Working Day</p>
              <p className="text-[13px] font-semibold text-[var(--text-primary)]">{format(new Date(ff.last_working_day), 'dd MMM yyyy')}</p>
            </div>
          )}
        </div>

        {/* Step stepper */}
        <div className="flex items-center gap-1">
          {STEPS.map((s, i) => {
            const done    = i < step;
            const current = i === step;
            return (
              <div key={s.id} className="flex items-center gap-1">
                <button onClick={() => setActiveStep(i)}
                  className={cn('flex items-center gap-1.5 h-7 px-2.5 rounded-full text-[11px] font-medium transition-all whitespace-nowrap',
                    current ? 'bg-[var(--accent)] text-white' :
                    done    ? 'bg-green-100 text-green-700 cursor-pointer hover:bg-green-200' :
                              'bg-[var(--bg-hover)] text-[var(--text-tertiary)]')}>
                  <span className={cn('w-4 h-4 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0',
                    done ? 'bg-green-200' : current ? 'bg-white/20' : '')}>
                    {done ? '✓' : s.id}
                  </span>
                  {s.label}
                </button>
                {i < STEPS.length - 1 && <ChevronRight size={11} className="text-[var(--text-tertiary)] shrink-0" />}
              </div>
            );
          })}
        </div>
      </div>

      {/* Step content */}
      <div className="flex-1 overflow-auto p-5">
        <div className="max-w-3xl mx-auto">
          <div className="card p-5">
            <h2 className="text-[15px] font-semibold text-[var(--text-primary)] mb-4 pb-3 border-b border-[var(--border)]">
              Step {step + 1}: {STEPS[step].label}
            </h2>

            {step === 0 && <Step1Initiation ff={ff} />}
            {step === 1 && <Step2Clearance ff={ff} onUpdate={handleUpdate} />}
            {step === 2 && <Step3Computation ff={ff} onUpdate={handleUpdate} />}
            {step === 3 && <Step4Documents ff={ff} />}
            {step === 4 && <Step5PF ff={ff} onUpdate={handleUpdate} />}
            {step === 5 && <Step6Confirm ff={ff} onClose={() => navigate('/documents/full-final')} />}

            {/* Navigation */}
            {step < 5 && (
              <div className="flex items-center justify-between mt-6 pt-4 border-t border-[var(--border)]">
                <button onClick={() => setActiveStep(Math.max(0, step - 1))}
                  disabled={step === 0}
                  className="h-9 px-4 text-[13px] font-medium border border-[var(--border)] rounded-[10px] hover:bg-[var(--bg-hover)] text-[var(--text-secondary)] disabled:opacity-40 transition-colors">
                  ← Previous
                </button>
                <button onClick={nextStep}
                  className="flex items-center gap-2 h-9 px-5 text-[13px] font-medium bg-[var(--accent)] text-white rounded-[10px] hover:bg-[var(--accent-hover)] transition-colors">
                  Save & Continue <ChevronRight size={14} />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
